import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { apiError } from "@/lib/apiClient";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, ExternalLink, RotateCw, GitBranch, Rocket, Loader2, Globe } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function Dashboard() {
  const [projects, setProjects] = useState(null);
  const [redeploying, setRedeploying] = useState("");
  const navigate = useNavigate();

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  useEffect(() => { load(); }, []);

  // Keep "Building" projects updating until the deploy finishes.
  useEffect(() => {
    if (!projects || !projects.some((p) => p.status === "Building")) return;
    const t = setInterval(async () => {
      const building = projects.filter((p) => p.status === "Building");
      const updated = await Promise.all(
        building.map((p) => api.get(`/projects/${p.id}`).then((r) => r.data).catch(() => null))
      );
      setProjects((prev) =>
        prev.map((p) => updated.find((u) => u && u.id === p.id) || p)
      );
    }, 8000);
    return () => clearInterval(t);
  }, [projects]);

  const redeploy = async (id) => {
    setRedeploying(id);
    try {
      await api.post(`/projects/${id}/deploy`);
      toast.success("Deploy triggered");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRedeploying("");
    }
  };

  return (
    <Layout>
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tighter">Projects</h1>
          <p className="text-sm text-neutral-400 mt-2">Deploy, monitor, and redeploy your sites.</p>
        </div>
        <Link
          to="/new"
          data-testid="new-project-button"
          className="inline-flex items-center gap-2 rounded-md bg-white text-black px-4 py-2 text-sm font-medium hover:bg-neutral-200 transition-colors duration-200"
        >
          <Plus className="h-4 w-4" /> New Project
        </Link>
      </div>

      {projects === null && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-40 rounded-lg border border-[#262626] bg-[#141414] animate-pulse" />
          ))}
        </div>
      )}

      {projects && projects.length === 0 && (
        <div className="rounded-lg border border-dashed border-[#262626] bg-[#0d0d0d] p-16 text-center" data-testid="empty-projects">
          <div className="h-12 w-12 rounded-lg bg-[#1F1F1F] border border-[#262626] flex items-center justify-center mx-auto mb-4">
            <Rocket className="h-6 w-6 text-neutral-400" strokeWidth={1.5} />
          </div>
          <h3 className="font-heading text-xl font-medium mb-2">No projects yet</h3>
          <p className="text-sm text-neutral-400 mb-6 max-w-sm mx-auto">
            Connect GitHub & Netlify, then deploy a repo in one click.
          </p>
          <button
            onClick={() => navigate("/new")}
            className="inline-flex items-center gap-2 rounded-md bg-white text-black px-4 py-2 text-sm font-medium hover:bg-neutral-200 transition-colors duration-200"
          >
            <Plus className="h-4 w-4" /> Deploy your first project
          </button>
        </div>
      )}

      {projects && projects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((p) => (
            <div
              key={p.id}
              data-testid={`project-card-${p.id}`}
              className="group rounded-lg border border-[#262626] bg-[#141414] p-6 hover:border-neutral-600 transition-colors duration-200 flex flex-col"
            >
              <div className="flex items-start justify-between mb-4">
                <Link to={`/projects/${p.id}`} className="min-w-0">
                  <h3 className="font-heading text-lg font-medium tracking-tight truncate group-hover:underline underline-offset-4">
                    {p.name}
                  </h3>
                  <p className="text-xs text-neutral-500 font-mono truncate flex items-center gap-1.5 mt-1">
                    <GitBranch className="h-3 w-3" /> {p.repo_full_name} · {p.branch}
                  </p>
                </Link>
                <StatusBadge status={p.status} />
              </div>

              {p.custom_domain ? (
                <a
                  href={`https://${p.custom_domain}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`project-domain-${p.id}`}
                  className="text-sm text-white hover:text-neutral-300 transition-colors duration-200 inline-flex items-center gap-1.5 truncate mb-1 font-medium"
                >
                  <Globe className="h-3.5 w-3.5 flex-shrink-0" /> {p.custom_domain} <ExternalLink className="h-3 w-3 flex-shrink-0" />
                </a>
              ) : null}
              {p.site_url ? (
                <a
                  href={p.site_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`project-url-${p.id}`}
                  className="text-sm text-neutral-400 hover:text-white transition-colors duration-200 inline-flex items-center gap-1.5 truncate mb-4"
                >
                  {p.site_url.replace("https://", "")} <ExternalLink className="h-3 w-3 flex-shrink-0" />
                </a>
              ) : (
                <p className="text-sm text-neutral-600 mb-4">No live URL yet</p>
              )}

              <div className="mt-auto flex items-center justify-between pt-4 border-t border-[#262626]">
                <span className="text-xs text-neutral-500">
                  Deployed {timeAgo(p.last_deploy?.created_at || p.updated_at)}
                </span>
                <button
                  onClick={() => redeploy(p.id)}
                  disabled={redeploying === p.id}
                  data-testid={`redeploy-${p.id}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-[#262626] px-3 py-1.5 text-xs text-neutral-300 hover:bg-[#1F1F1F] transition-colors duration-200 disabled:opacity-50"
                >
                  {redeploying === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
                  Redeploy
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
